import { useCallback, useRef, useState } from "react";

const DEFAULT_THROTTLE_DELAY_MS = 50;

type TThrottler = (func: (...args: any[]) => any, delay?: number) => any;

/**
 * Return a memoized throttle function
 *
 * @returns
 */
export const useThrottle = (): TThrottler => {
    const throttleRef = useRef<boolean>(false);
    return useCallback((func, delay = DEFAULT_THROTTLE_DELAY_MS) => {
        if (throttleRef.current) {
            return;
        }
        throttleRef.current = true;
        func();
        setTimeout(() => {
            throttleRef.current = false;
        }, delay);
    }, []);
};

/**
 * Return a memoized throttle function
 *
 * @returns
 */
type TFunc = (...args: any[]) => unknown;
export const useThrottleFunction = (
    func: TFunc | undefined,
    delay = DEFAULT_THROTTLE_DELAY_MS,
    defaultResponseValue: any = undefined,
): ((...args: any[]) => unknown) => {
    const throttleRef = useRef<boolean>(false);
    return useCallback(
        (...args: any[]) => {
            if (!func) return;

            if (throttleRef.current) {
                return defaultResponseValue;
            }
            throttleRef.current = true;
            func(...args);
            setTimeout(() => {
                throttleRef.current = false;
            }, delay);
        },
        [func, delay, defaultResponseValue],
    );
};

/**
 * Returns a throttled function and a boolean indicating if it's throttled.
 *
 * @param func - The function to throttle.
 * @param delay - The throttle delay in milliseconds.
 * @returns [throttledFunction, isThrottled]
 */
export const useThrottledFunctionWithIsThrottled = <T extends (...args: any[]) => any>(
    func: T,
    delay = DEFAULT_THROTTLE_DELAY_MS,
): [T, boolean] => {
    const [isThrottled, setIsThrottled] = useState(false);
    const throttleRef = useRef<boolean>(false);

    const throttledFunc = useCallback(
        (...args: Parameters<T>) => {
            if (throttleRef.current) {
                return;
            }
            throttleRef.current = true;
            setIsThrottled(true);
            func(...args);
            setTimeout(() => {
                throttleRef.current = false;
                setIsThrottled(false);
            }, delay);
        },
        [func, delay],
    );

    return [throttledFunc as T, isThrottled];
};

/**
 * Return a memoized throttle function
 *
 * @returns
 */
export const useThrottleAsyncFunction = (
    func: (...args: any[]) => Promise<unknown>,
    delay = DEFAULT_THROTTLE_DELAY_MS,
    defaultResponseValue: any = undefined,
): ((...args: any[]) => Promise<unknown>) => {
    const throttleRef = useRef<boolean>(false);
    return useCallback(
        async (...args: any[]) => {
            if (throttleRef.current) {
                return defaultResponseValue;
            }
            throttleRef.current = true;
            func(...args).catch(console.error);
            setTimeout(() => {
                throttleRef.current = false;
            }, delay);
        },
        [func, delay, defaultResponseValue],
    );
};
